import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { Menu, X, Download, User, LogOut, FileText } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth, useSettings } from "@/context/AppContext";

const NAV = [
  ["/como-funciona", "Como funciona"],
  ["/areas-juridicas", "Áreas jurídicas"],
  ["/empresas", "Para empresas"],
  ["/instalar", "Instalar app"],
];

const Header = () => {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loggedUser = user && user.role !== "admin";
  return (
    <header className="sticky top-0 z-40 border-b border-white/10 bg-[#092C66]/95 text-white backdrop-blur-md">
      <div className="wrap flex h-16 items-center justify-between">
        <Link to="/" data-testid="header-logo-link"><Logo light /></Link>
        <nav className="hidden items-center gap-1 lg:flex">
          {NAV.map(([to, label]) => (
            <NavLink key={to} to={to} data-testid={`nav-${to.slice(1)}`}
              className={({ isActive }) => `rounded-lg px-3 py-2 text-sm font-medium transition-colors ${isActive ? "bg-white/15" : "hover:bg-white/10"}`}>
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden items-center gap-2 lg:flex">
          {loggedUser ? (
            <>
              <Link to={user.role === "lawyer" ? "/advogado" : "/minhas-solicitacoes"} className="rounded-lg px-3 py-2 text-sm font-semibold hover:bg-white/10" data-testid="nav-my-requests">
                {user.role === "lawyer" ? "Portal do advogado" : "Minhas solicitações"}
              </Link>
              <button onClick={async () => { await logout(); nav("/"); }} className="rounded-lg p-2 hover:bg-white/10" aria-label="Sair" data-testid="nav-logout-btn"><LogOut size={18} /></button>
            </>
          ) : (
            <Link to="/entrar" className="rounded-lg px-3 py-2 text-sm font-semibold hover:bg-white/10" data-testid="nav-login-link">Entrar</Link>
          )}
          <Link to="/solicitar" className="rounded-xl bg-[#1779E6] px-4 py-2.5 text-sm font-bold transition-colors hover:bg-[#3198EF]" data-testid="nav-start-request-btn">Iniciar solicitação</Link>
        </div>
        <button className="rounded-lg p-2 lg:hidden" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="mobile-menu-toggle">
          {open ? <X /> : <Menu />}
        </button>
      </div>
      {open && (
        <div className="border-t border-white/10 bg-[#092C66] lg:hidden" data-testid="mobile-menu">
          <div className="wrap flex flex-col py-3">
            {NAV.map(([to, label]) => (
              <Link key={to} to={to} onClick={() => setOpen(false)} className="rounded-lg px-2 py-3 text-base font-medium hover:bg-white/10" data-testid={`mobile-nav-${to.slice(1)}`}>{label}</Link>
            ))}
            {loggedUser ? (
              <>
                <Link to="/minhas-solicitacoes" onClick={() => setOpen(false)} className="rounded-lg px-2 py-3 font-medium" data-testid="mobile-nav-my-requests">Minhas solicitações</Link>
                <Link to="/minha-conta" onClick={() => setOpen(false)} className="rounded-lg px-2 py-3 font-medium" data-testid="mobile-nav-account">Minha conta</Link>
                <button onClick={async () => { await logout(); setOpen(false); nav("/"); }} className="rounded-lg px-2 py-3 text-left font-medium" data-testid="mobile-nav-logout">Sair</button>
              </>
            ) : (
              <Link to="/entrar" onClick={() => setOpen(false)} className="rounded-lg px-2 py-3 font-medium" data-testid="mobile-nav-login">Entrar</Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

const Footer = () => {
  const { settings } = useSettings() || {};
  const name = settings?.brand?.name || "Conexão Litigium";
  return (
    <footer className="mt-20 bg-[#092C66] pb-24 pt-14 text-white lg:pb-14" data-testid="site-footer">
      <div className="wrap grid gap-10 md:grid-cols-4">
        <div className="md:col-span-2">
          <Logo light />
          <p className="mt-4 max-w-md text-sm leading-relaxed text-[#EDF5FF]/80">
            Plataforma tecnológica gratuita para cadastro, envio e acompanhamento de solicitações jurídicas. O uso da plataforma não substitui
            a análise profissional nem garante a aceitação de representação.
          </p>
        </div>
        <div className="flex flex-col gap-2 text-sm">
          <span className="font-display font-bold">Plataforma</span>
          <Link to="/como-funciona" className="text-[#EDF5FF]/80 hover:text-white">Como funciona</Link>
          <Link to="/empresas/consultoria-recorrente" className="text-[#EDF5FF]/80 hover:text-white">Consultoria recorrente</Link>
          <Link to="/empresas/solicitacao-pontual" className="text-[#EDF5FF]/80 hover:text-white">Solicitação pontual</Link>
          <Link to="/instalar" className="text-[#EDF5FF]/80 hover:text-white">Instale a {name}</Link>
          {settings?.lawyer_signup_open && <Link to="/advogado" className="text-[#EDF5FF]/80 hover:text-white">Portal do advogado</Link>}
        </div>
        <div className="flex flex-col gap-2 text-sm">
          <span className="font-display font-bold">Transparência</span>
          <Link to="/privacidade" className="text-[#EDF5FF]/80 hover:text-white" data-testid="footer-privacy-link">Política de Privacidade</Link>
          <Link to="/termos" className="text-[#EDF5FF]/80 hover:text-white" data-testid="footer-terms-link">Termos de Uso</Link>
        </div>
      </div>
      <div className="wrap mt-10 border-t border-white/10 pt-6 text-xs text-[#EDF5FF]/60">© {new Date().getFullYear()} {name}. Identidade comercial independente.</div>
    </footer>
  );
};

const MobileBar = () => {
  const { user } = useAuth();
  return (
    <div className="safe-bottom fixed inset-x-0 bottom-0 z-40 border-t border-[#3198EF]/20 bg-white/95 backdrop-blur-md lg:hidden" data-testid="mobile-bottom-bar">
      <div className="grid grid-cols-3 text-xs font-semibold text-[#092C66]">
        <Link to="/solicitar" className="flex flex-col items-center gap-1 py-2.5 text-[#1465C8]" data-testid="bottom-new-request"><FileText size={20} />Nova solicitação</Link>
        <Link to={user ? "/minhas-solicitacoes" : "/entrar"} className="flex flex-col items-center gap-1 py-2.5" data-testid="bottom-my-area"><User size={20} />Minha área</Link>
        <Link to="/instalar" className="flex flex-col items-center gap-1 py-2.5" data-testid="bottom-install"><Download size={20} />Instalar</Link>
      </div>
    </div>
  );
};

export default function PublicLayout() {
  return (
    <div className="flex min-h-screen flex-col bg-white">
      <Header />
      <main className="flex-1"><Outlet /></main>
      <Footer />
      <MobileBar />
    </div>
  );
}
